import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { PillPaletteToken } from "../config/palette";

export interface SingleSelectPillOption {
  id: string;
  value: string;
  label: string;
  colorKey?: string;
  disabled?: boolean;
}

export function SingleSelectPillField({
  id,
  value,
  options,
  onChange,
  ariaLabel,
  ariaDescribedBy,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  resolveColor,
  disabled = false,
  invalid = false,
  clearable = false,
}: {
  id: string;
  value: string;
  options: readonly SingleSelectPillOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  ariaDescribedBy?: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  resolveColor: (option: SingleSelectPillOption) => PillPaletteToken;
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(normalizedSearch),
  );

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    setSearch("");
    setActiveIndex(0);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  const show = () => {
    if (disabled) return;
    setSearch("");
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((option) => option.value === value),
      ),
    );
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const handleMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  useEffect(() => {
    if (activeIndex >= filteredOptions.length) setActiveIndex(0);
  }, [activeIndex, filteredOptions.length]);

  const select = (option: SingleSelectPillOption) => {
    if (option.disabled) return;
    onChange(option.value);
    close(true);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!filteredOptions.length) return;
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(
        (current) =>
          (current + direction + filteredOptions.length) %
          filteredOptions.length,
      );
      return;
    }
    if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      select(filteredOptions[activeIndex]);
    }
  };

  const activeOption = filteredOptions[activeIndex];

  return (
    <div className="cp-multi-select cp-single-select" ref={rootRef}>
      <div
        className="cp-multi-select__field"
        data-open={open || undefined}
        data-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        onClick={show}
      >
        <div className="cp-multi-select__values">
          {selectedOption ? (
            <span
              className="cp-value-pill cp-multi-select__pill cp-single-select__pill"
              data-palette={resolveColor(selectedOption)}
            >
              <span>{selectedOption.label}</span>
              {clearable ? (
                <button
                  type="button"
                  aria-label={`Clear ${selectedOption.label}`}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange("");
                  }}
                >
                  <X aria-hidden="true" />
                </button>
              ) : null}
            </span>
          ) : (
            <span className="cp-multi-select__placeholder">{placeholder}</span>
          )}
        </div>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className="cp-multi-select__trigger"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          onClick={show}
          onKeyDown={(event) => {
            if (
              event.key === "ArrowDown" ||
              event.key === "Enter" ||
              event.key === " "
            ) {
              event.preventDefault();
              show();
            }
          }}
        >
          <ChevronDown aria-hidden="true" />
        </button>
      </div>

      {open ? (
        <div className="cp-multi-select__popover">
          <div className="cp-multi-select__search">
            <input
              ref={searchRef}
              type="search"
              role="combobox"
              aria-label={searchPlaceholder}
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={
                activeOption ? `${listboxId}-${activeOption.id}` : undefined
              }
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <div
            id={listboxId}
            className="cp-multi-select__options"
            role="listbox"
            aria-label={`${ariaLabel} options`}
          >
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => {
                const selected = value === option.value;
                return (
                  <button
                    id={`${listboxId}-${option.id}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className="cp-multi-select__option cp-single-select__option"
                    data-palette={resolveColor(option)}
                    data-active={index === activeIndex || undefined}
                    data-disabled={option.disabled || undefined}
                    disabled={option.disabled}
                    key={option.id}
                    onClick={() => select(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span
                      className="cp-single-select__check"
                      aria-hidden="true"
                    >
                      {selected ? <Check /> : null}
                    </span>
                    <span
                      className="cp-value-pill"
                      data-palette={resolveColor(option)}
                    >
                      {option.label}
                    </span>
                    {option.disabled ? (
                      <span className="cp-multi-select__unavailable">
                        Unavailable — retained historical value
                      </span>
                    ) : null}
                  </button>
                );
              })
            ) : (
              <p className="cp-multi-select__empty">{emptyMessage}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
